import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EmptyState, StatusBadge } from '@pazportal/ui';
import { useAllTickets } from '../hooks/useAllTickets';

export function TicketsPage() {
  const navigate = useNavigate();
  const { id: routeLocationId } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const queryLocation = searchParams.get('location');
  const locationId = routeLocationId ?? undefined;
  const ticketHref = (ticketId: string) =>
    locationId ? `/locations/${locationId}/tickets/${ticketId}` : `/tickets/${ticketId}`;
  const { data: tickets, isLoading, isError } = useAllTickets({ location_id: locationId });
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');

  const visibleTickets = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return (tickets ?? []).filter((ticket) => {
      if (status !== 'all' && ticket.status !== status) return false;
      if (!normalized) return true;
      return [
        ticket.ticket_number,
        ticket.title,
        ticket.customer?.name,
        ticket.location?.name,
      ].filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(normalized));
    });
  }, [query, status, tickets]);

  if (!routeLocationId && queryLocation) {
    return <Navigate to={`/locations/${queryLocation}/tickets`} replace />;
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center gap-3 text-sm text-text-muted">
        <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
        Loading tickets…
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        icon={<TicketIcon />}
        title="Unable to load tickets"
        subtitle="The ticket records are still in PazPortal. Refresh to try again."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-text sm:text-2xl">Tickets</h1>
        <p className="mt-1 text-sm text-text-muted">
          {(tickets ?? []).length} ticket{(tickets ?? []).length === 1 ? '' : 's'}
          {locationId ? ' tied to this site' : ' across all customers and sites'}
        </p>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <label className="flex-1">
          <span className="sr-only">Search tickets</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search ticket, customer, or site"
            className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm text-text outline-none focus:ring-2 focus:ring-primary"
          />
        </label>
        <label>
          <span className="sr-only">Filter by status</span>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm text-text sm:w-56"
          >
            <option value="all">All statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="waiting_on_customer">Waiting on customer</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </label>
      </div>

      {visibleTickets.length === 0 ? (
        <div className="rounded-lg border border-border bg-surface">
          <EmptyState
            icon={<TicketIcon />}
            title={(query || status !== 'all') ? 'No matching tickets' : 'No tickets'}
            subtitle={(query || status !== 'all') ? 'Try another search or status.' : 'Customer support tickets appear here.'}
          />
        </div>
      ) : (
        <>
          <div className="space-y-2 md:hidden">
            {visibleTickets.map((ticket) => (
              <button
                key={ticket.id}
                type="button"
                onClick={() => navigate(ticketHref(ticket.id))}
                className="w-full rounded-lg border border-border bg-surface p-4 text-left"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-mono text-text-faint">{ticket.ticket_number}</p>
                    <p className="truncate font-medium text-text">{ticket.title}</p>
                  </div>
                  <StatusBadge status={ticket.status ?? 'open'} />
                </div>
                <p className="mt-2 text-sm text-text-muted">
                  {ticket.customer?.name ?? 'Unmapped customer'} · {ticket.location?.name ?? 'No site'}
                </p>
              </button>
            ))}
          </div>

          <div className="hidden overflow-x-auto rounded-lg border border-border md:block">
            <table className="w-full text-sm">
              <thead className="bg-surface-3 text-left text-text-muted">
                <tr>
                  <th className="px-4 py-3 font-medium">Ticket</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Site</th>
                  <th className="px-4 py-3 font-medium">Priority</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider bg-surface">
                {visibleTickets.map((ticket) => (
                  <tr key={ticket.id} className="hover:bg-surface-strong">
                    <td className="px-4 py-3">
                      <p className="text-xs font-mono text-text-faint">{ticket.ticket_number}</p>
                      <Link to={ticketHref(ticket.id)} className="font-medium text-primary hover:underline">
                        {ticket.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      {ticket.customer ? (
                        <Link to={`/customers/${ticket.customer.id}`} className="text-primary hover:underline">
                          {ticket.customer.name}
                        </Link>
                      ) : (
                        <span className="text-text-muted">Unmapped customer</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {ticket.location ? (
                        <Link to={`/locations/${ticket.location.id}`} className="text-primary hover:underline">
                          {ticket.location.name}
                        </Link>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={ticket.priority ?? 'medium'} /></td>
                    <td className="px-4 py-3"><StatusBadge status={ticket.status ?? 'open'} /></td>
                    <td className="px-4 py-3 text-text-muted">{new Date(ticket.updated_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function TicketIcon() {
  return (
    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z" />
    </svg>
  );
}
